import React from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';

import { UnreachableFunction } from 'ui/base/types';
import { defaultString } from 'ui/base/types/strings';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ToastID, defaultToast } from 'ui/base/toast';
import { Link } from 'ui/base/link';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';

import { SwitchInput } from 'ui/standard/input/switch_input';
import { getOSString } from 'ui/standard/input/os_input/os_input_data';

import { DeviceIcon } from 'ui/devices/device_icon/device_icon';
import { HostNetworkThroughput } from 'ui/devices/network_throughput/network_throughput';

import { ResultKind } from 'services/base/types';
import { BasePath } from 'services/base/page_path';

import { Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { DeviceType, Host, IPangolinDomain } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';
import { HostDeviceType } from 'services/pangolin/protos/hosts_pb';
import { HostsUpdateResult, hostDisplayDeviceType } from 'services/pangolin/hosts_service';

import { Redirect } from 'pages/core/redirect/redirect';

import { ViewDeviceStore } from './view_device_store';
import baseStyles from './view_device.module.css';
import Messages from './view_device_messages';

type ViewDeviceStyles = {
  headerContainer: string;
  disabledLabel: string;
  link: string;
  deviceIconContainer: string;
  iconLayoutContainer: string;
  deviceNameContainer: string;
  deviceInfoContainer: string;
  desktopIcon: string;
  miscIcon: string;
  cameraIcon: string;
  gamingIcon: string;
  mediaIcon: string;
  mobileIcon: string;
  networkIcon: string;
  printerIcon: string;
  titleContainer: string;
  row: string;
  column: string;
  label: string;
  value: string;
  devicePermissionContainer: string;
  devicePermissionRow: string;
  devicePermissionLabel: string;
  optionalFeaturesContainer: string;
};

const themedStyles: ThemedStyles<ViewDeviceStyles> = {
  [Theme.DAYLIGHT]: {
    headerContainer: baseStyles.headerContainer,
    disabledLabel: baseStyles.daylightDisabledLabel,
    link: baseStyles.daylightLink,
    deviceIconContainer: baseStyles.daylightIconContainer,
    iconLayoutContainer: baseStyles.iconLayoutContainer,
    deviceNameContainer: baseStyles.deviceNameContainer,
    deviceInfoContainer: baseStyles.deviceInfoContainer,
    desktopIcon: baseStyles.daylightDesktopIcon,
    miscIcon: baseStyles.daylightMiscIcon,
    cameraIcon: baseStyles.daylightCameraIcon,
    gamingIcon: baseStyles.daylightGamingIcon,
    mediaIcon: baseStyles.daylightMediaIcon,
    mobileIcon: baseStyles.daylightMobileIcon,
    networkIcon: baseStyles.daylightNetworkIcon,
    printerIcon: baseStyles.daylightPrinterIcon,
    titleContainer: baseStyles.daylightTitleContainer,
    row: baseStyles.row,
    column: baseStyles.column,
    label: baseStyles.daylightLabel,
    value: baseStyles.value,
    devicePermissionContainer: baseStyles.devicePermissionContainer,
    devicePermissionRow: baseStyles.devicePermissionRow,
    devicePermissionLabel: baseStyles.devicePermissionLabel,
    optionalFeaturesContainer: baseStyles.optionalFeaturesContainer,
  },
  [Theme.MIDNIGHT]: {
    headerContainer: baseStyles.headerContainer,
    disabledLabel: baseStyles.midnightDisabledLabel,
    link: baseStyles.midnightLink,
    deviceIconContainer: baseStyles.midnightIconContainer,
    iconLayoutContainer: baseStyles.iconLayoutContainer,
    deviceNameContainer: baseStyles.deviceNameContainer,
    deviceInfoContainer: baseStyles.deviceInfoContainer,
    desktopIcon: baseStyles.midnightDesktopIcon,
    miscIcon: baseStyles.midnightMiscIcon,
    cameraIcon: baseStyles.midnightCameraIcon,
    gamingIcon: baseStyles.midnightGamingIcon,
    mediaIcon: baseStyles.midnightMediaIcon,
    mobileIcon: baseStyles.midnightMobileIcon,
    networkIcon: baseStyles.midnightNetworkIcon,
    printerIcon: baseStyles.midnightPrinterIcon,
    titleContainer: baseStyles.midnightTitleContainer,
    row: baseStyles.row,
    column: baseStyles.column,
    label: baseStyles.midnightLabel,
    value: baseStyles.value,
    devicePermissionContainer: baseStyles.devicePermissionContainer,
    devicePermissionRow: baseStyles.devicePermissionRow,
    devicePermissionLabel: baseStyles.devicePermissionLabel,
    optionalFeaturesContainer: baseStyles.optionalFeaturesContainer,
  },
};

export function getDeviceTypeString(deviceType: DeviceType) {
  switch(deviceType) {
    case HostDeviceType.H_DESKTOP:
      return Messages.desktop();
    case HostDeviceType.H_MISC:
      return Messages.misc();
    case HostDeviceType.H_CAMERA:
      return Messages.camera();
    case HostDeviceType.H_GAMING:
      return Messages.gaming();
    case HostDeviceType.H_MEDIA:
      return Messages.media();
    case HostDeviceType.H_MOBILE:
      return Messages.mobile();
    case HostDeviceType.H_NETWORK:
      return Messages.network();
    case HostDeviceType.H_PRINTER:
      return Messages.printer();
    case HostDeviceType.H_VPN:
      return Messages.vpn();
    case HostDeviceType.H_UNKNOWN:
      return Messages.unknown();
    default:
      return UnreachableFunction(deviceType);
  }
}


type PairedDetailsProps = {
  host: Host;
  pangolin: Pangolin;
};
const PairedDetails = ({ pangolin, host }: PairedDetailsProps) => {
  const styles = useStyles(themedStyles);

  const details: { label: string; value: string }[] = [
    {
      label: Messages.ipAddressLabel(),
      value: host.ip,
    },
    {
      label: Messages.macAddressLabel(),
      value: host.mac,
    },
    {
      label: Messages.deviceTypeLabel(),
      value: getDeviceTypeString(hostDisplayDeviceType(host)),
    },
    {
      label: Messages.operatingSystemLabel(),
      value: defaultString(getOSString(host.customOS ?? '', ''), host.humanReadableOS),
    },
    {
      label: Messages.connectedToLabel(),
      value: pangolin.nickname,
    },
    {
      label: Messages.lastSeenLabel(),
      value: host.lastSeen == null ? Messages.lastSeenUnknown() : moment(host.lastSeen).fromNow(),
    },
  ];

  return (
    <div className={styles.row}>
      {details.map((item, index) => {
        return (
          <div className={styles.column} key={index}>
            <div className={styles.label}>{item.label}</div>
            <div className={styles.value}>{item.value}</div>
          </div>
        );
      })}
      { details.length % 2 === 0 ? null : <div className={styles.column}></div> }
    </div>
  );
};

type DeviceInfoProps = {
  host: Host;
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
const DeviceInfo = ({ host, pangolin, domain }: DeviceInfoProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.deviceInfoContainer}>
      <div className={styles.titleContainer}>
        {Messages.deviceInfoTitle()}
      </div>
      <PairedDetails pangolin={pangolin} host={host}/>
      <HostNetworkThroughput pangolin={domain} host={host}/>
    </div>
  );
};

type DevicePermissionProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  host: Host;
  store: ViewDeviceStore;
};

const DevicePermission = observer(({ pangolin, domain, host, store }: DevicePermissionProps) => {
  const styles = useStyles(themedStyles);

  const handleUpdate = React.useCallback(async(action: () => Promise<HostsUpdateResult>) => {
    const result = await action();

    // display toast error messages if the result is not OK
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      return true;
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      return false;
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      return false;
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
      return false;
    }
  }, []);

  const makeUpdateHandler = <T, >(action: (domain: IPangolinDomain, value: T) => Promise<HostsUpdateResult>) => {
    return (value: T) => handleUpdate(() => action(domain, value));
  };

  return (
    <div className={styles.devicePermissionContainer}>
      <div className={styles.titleContainer}>
        {Messages.devicePermissionTitle()}
      </div>
      {store.blockInternet !== undefined && (
        <div className={styles.devicePermissionRow}>
          <div className={classNames(styles.devicePermissionLabel, { [styles.disabledLabel]: !pangolin.onlineStatus })}>{Messages.blockInternet()}</div>
          <div>
            <SwitchInput
              busy={store.busyBlockInternet}
              disabled={!pangolin.onlineStatus || !!host.ignoreAll || !!store.ignoreAll?.value}
              reserveBusyIconArea={true}
              store={store.blockInternet}
              onChange={makeUpdateHandler(store.handleBlockInternetChange)}/>
          </div>
        </div>
      )}
      {store.blockInternal !== undefined && (
        <div className={styles.devicePermissionRow}>
          <div className={classNames(styles.devicePermissionLabel, { [styles.disabledLabel]: !pangolin.onlineStatus })}>{Messages.blockInternal()}</div>
          <div>
            <SwitchInput
              busy={store.busyBlockInternal}
              disabled={!pangolin.onlineStatus || !!host.ignoreAll || !!store.ignoreAll?.value}
              reserveBusyIconArea={true}
              store={store.blockInternal}
              onChange={makeUpdateHandler(store.handleBlockInternalChange)}/>
          </div>
        </div>
      )}
      {store.ignoreSchedules !== undefined && (
        <div className={styles.devicePermissionRow}>
          <div className={classNames(styles.devicePermissionLabel, { [styles.disabledLabel]: !pangolin.onlineStatus })}>{Messages.ignoreSchedules()}</div>
          <div>
            <SwitchInput
              busy={store.busyIgnoreSchedules}
              disabled={!pangolin.onlineStatus || !!host.ignoreAll || !!store.ignoreAll?.value}
              reserveBusyIconArea={true}
              store={store.ignoreSchedules}
              onChange={makeUpdateHandler(store.handleIgnoreSchedulesChange)}/>
          </div>
        </div>
      )}
      {store.ignoreAll !== undefined && (
        <div className={styles.devicePermissionRow}>
          <div className={classNames(styles.devicePermissionLabel, { [styles.disabledLabel]: !pangolin.onlineStatus })}>{Messages.ignoreAll()}</div>
          <div>
            <SwitchInput
              busy={store.busyIgnoreAll}
              disabled={!pangolin.onlineStatus}
              reserveBusyIconArea={true}
              store={store.ignoreAll}
              onChange={makeUpdateHandler(store.handleIgnoreAllChange)}/>
          </div>
        </div>
      )}
    </div>
  );
});

type OptionalFeatureProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  host: Host;
  store: ViewDeviceStore;
};

const OptionalFeature = observer(({ pangolin, domain, host, store }: OptionalFeatureProps) => {
  const styles = useStyles(themedStyles);

  const handleUpdate = React.useCallback(async(action: () => Promise<HostsUpdateResult>) => {
    const result = await action();

    // display toast error messages if the result is not OK
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      return true;
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      return false;
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      return false;
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
      return false;
    }
  }, []);

  const makeUpdateHandler = <T, >(action: (domain: IPangolinDomain, value: T) => Promise<HostsUpdateResult>) => {
    return (value: T) => handleUpdate(() => action(domain, value));
  };

  return (
    <div className={styles.optionalFeaturesContainer}>
      <div className={styles.titleContainer}>
        {Messages.optionalFeaturesTitle()}
      </div>
      {store.blockAdTracking !== undefined && (
        <div className={styles.devicePermissionRow}>
          <div className={classNames(styles.devicePermissionLabel, { [styles.disabledLabel]: !pangolin.onlineStatus })}>{Messages.blockAdTracking()}</div>
          <div>
            <SwitchInput
              busy={store.busyBlockAdTracking}
              disabled={!pangolin.onlineStatus || !!host.ignoreAll || !!store.ignoreAll?.value}
              reserveBusyIconArea={true}
              store={store.blockAdTracking}
              onChange={makeUpdateHandler(store.handleBlockAdTrackingChange)}/>
          </div>
        </div>
      )}
      {store.blockMalicious !== undefined && (
        <div className={styles.devicePermissionRow}>
          <div className={classNames(styles.devicePermissionLabel, { [styles.disabledLabel]: !pangolin.onlineStatus })}>{Messages.blockMaliciousTraffic()}</div>
          <div>
            <SwitchInput
              busy={store.busyBlockMalicious}
              disabled={!pangolin.onlineStatus || !!host.ignoreAll || !!store.ignoreAll?.value}
              reserveBusyIconArea={true}
              store={store.blockMalicious}
              onChange={makeUpdateHandler(store.handleBlockMaliciousFeaturesChange)}/>
          </div>
        </div>
      )}
      {store.notifyOnUp !== undefined && (
        <div className={styles.devicePermissionRow}>
          <div className={classNames(styles.devicePermissionLabel, { [styles.disabledLabel]: !pangolin.onlineStatus })}>{Messages.notifyOnUp()}</div>
          <div>
            <SwitchInput
              busy={store.busyNotifyOnUp}
              disabled={!pangolin.onlineStatus || !!host.ignoreAll || !!store.ignoreAll?.value}
              reserveBusyIconArea={true}
              store={store.notifyOnUp}
              onChange={makeUpdateHandler(store.handleNotifyOnUpChange)}/>
          </div>
        </div>
      )}
      {store.masquerade !== undefined && (
        <div className={styles.devicePermissionRow}>
          <div className={classNames(styles.devicePermissionLabel, { [styles.disabledLabel]: !pangolin.onlineStatus })}>{Messages.masquerade()}</div>
          <div>
            <SwitchInput
              busy={store.busyMasquerade}
              disabled={!pangolin.onlineStatus || !!host.ignoreAll || !!store.ignoreAll?.value}
              reserveBusyIconArea={true}
              store={store.masquerade}
              onChange={makeUpdateHandler(store.handleMasqueradeChange)}/>
          </div>
        </div>
      )}
    </div>
  );
});

type BaseViewDeviceProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  host: Host;
};

export const BaseViewDevice = observer(({ pangolin, domain, host }: BaseViewDeviceProps) => {
  const styles = useStyles(themedStyles);

  const [store, setStore] = React.useState(() => new ViewDeviceStore(host));
  React.useEffect(() => { setStore(new ViewDeviceStore(host)); }, [host]);

  return (
    <StandardPage>
      <div className={styles.headerContainer}>
        <BackButton/>
        <Link
          path={{ kind: BasePath.DEVICES_EDIT, pangolinID: pangolin.id, deviceID: host.id }}
          className={styles.link}
          disabled={!pangolin.onlineStatus}>
          {Messages.edit()}
        </Link>
      </div>
      <DeviceIcon type={hostDisplayDeviceType(host)}/>
      <div className={styles.deviceNameContainer}>
        {host.customName?.trim() ?? host.name}
      </div>
      <DeviceInfo
        pangolin={pangolin}
        domain={domain}
        host={host}
      />
      <DevicePermission
        pangolin={pangolin}
        domain={domain}
        host={host}
        store={store}
      />
      <OptionalFeature
        pangolin={pangolin}
        domain={domain}
        host={host}
        store={store}
      />
    </StandardPage>
  );
});

export const ViewDevice = observer(() => {
  const { pangolinID, deviceID } = useParams<{ pangolinID: string, deviceID: string }>();
  if (!pangolinID) return <Redirect />;
  if (!deviceID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  const host = domain.hosts.get(deviceID);
  if (!host) return <Redirect />;

  return (
    <BaseViewDevice
      pangolin={pangolin}
      domain={domain}
      host={host}
    />
  );
});
